<template>
    <div class="modal-overlay" @click.self="$emit('close')">
      <div class="modal-content">
        <div class="modal-header">
            <h3>
                <i class="fas fa-fire"></i>
                {{ isEditing ? 'Edit Hot List' : 'Create Hot List' }}
              </h3>
          <button @click="$emit('close')" class="close-btn">×</button>
        </div>
  
        <div class="modal-body">
          <!-- ✅ FIXED: Sharing section -->
          <div v-if="!isEditing" class="form-group sharing-section">
            <label>
              <i class="fas fa-share-alt"></i>
              Sharing
            </label>
            <div class="sharing-options">
              <label class="radio-option" :class="{ selected: sharingMode === 'personal' }">
                <input 
                  type="radio"
                  name="sharing"
                  value="project"
                  v-model="sharingMode"
                  @change="onSharingModeChange"
                />
                <span class="option-content">
                  <strong>Personal</strong>
                  <small>Only you can see this hot list</small>
                </span>
              </label>
              
              <label 
                v-if="availableProjects.length > 0" 
                class="radio-option"
                :class="{ selected: sharingMode === 'project' }"
              >
                <input 
                  type="radio" 
                  name="sharing"
                  value="project"
                  v-model="sharingMode"
                  @change="onSharingModeChange"
                />
                <span class="option-content">
                  <strong>Share with Client</strong>
                  <small>Client can view (but not edit) this hot list</small>
                </span>
              </label>
              
              <!-- ✅ Show message if no projects available -->
              <div v-else class="no-projects-message">
                <i class="fas fa-info-circle"></i>
                <span>No active projects available. Create a personal hot list or wait for project assignment.</span>
              </div>
            </div>
            
            <!-- ✅ Project selector (only when sharing) -->
            <transition name="slide-down">
              <div v-if="sharingMode === 'project'" class="project-selector">
                <label>Select Project</label>
                <select v-model="selectedProjectId" required>
                  <option value="">Choose a project...</option>
                  <option 
                    v-for="project in availableProjects" 
                    :key="project.project_id"
                    :value="project.project_id"
                  >
                    {{ project.client_name }} - {{ project.project_name }}
                  </option>
                </select>
              </div>
            </transition>
          </div>





  
          <form @submit.prevent="createHotList">
            <!-- Name -->
            <div class="form-group">
              <label>
                <i class="fas fa-tag"></i>
                List Name *
              </label>
              <input
                v-model="formData.name"
                type="text"
                placeholder="e.g., Senior Java Developers Q4 2025"
                required
                maxlength="100"
              />
            </div>
  
            <!-- Job Title -->
            <div class="form-group">
              <label>
                <i class="fas fa-briefcase"></i>
                Job Title *
              </label>
              <input
                v-model="formData.criteria.job_title"
                type="text"
                placeholder="e.g., Senior Java Developer"
                required
                maxlength="100"
              />
              <small>Main position you're looking for</small>
            </div>
  
            <!-- Job Description (Detailed) -->
            <!-- <div class="form-group">
              <label>
                <i class="fas fa-file-alt"></i>
                Job Description *
              </label>
              <textarea
                v-model="formData.criteria.job_description"
                rows="4"
                placeholder="Describe the role, responsibilities, and what the ideal candidate looks like..."
                required
              ></textarea>
              <small>Detailed description helps AI matching find better candidates</small>
            </div> -->
            <!-- NEW: Multiple responsibility bullets (like skills) -->
                    <!-- CHANGED: Job Description now uses tag input like skills -->
            <div class="form-group">
                <label>
                <i class="fas fa-tasks"></i>
                Key Responsibilities *
                </label>
                <div class="skills-input">
                <div class="skills-tags">
                    <span
                    v-for="(resp, index) in formData.criteria.job_description"
                    :key="index"
                    class="skill-tag responsibility"
                    >
                    {{ resp }}
                    <button @click="removeResponsibility(index)" type="button">×</button>
                    </span>
                </div>
                <input
                    v-model="responsibilityInput"
                    @keydown.enter.prevent="addResponsibility"
                    type="text"
                    placeholder="Add responsibility and press Enter (e.g., 'Build REST APIs')"
                />
                </div>
                <small>Add 3-5 key responsibilities separately for better matching</small>
            </div>

            <!-- Required Skills -->
            <div class="form-group">
              <label>
                <i class="fas fa-code"></i>
                Required Skills *
              </label>
              <div class="skills-input">
                <div class="skills-tags">
                  <span
                    v-for="(skill, index) in formData.criteria.skills"
                    :key="index"
                    class="skill-tag required"
                  >
                    {{ skill }}
                    <button @click="removeSkill(index, 'skills')" type="button">×</button>
                  </span>
                </div>
                <input
                  v-model="skillInput"
                  @keydown.enter.prevent="addSkill('skills')"
                  @keydown.comma.prevent="addSkill('skills')"
                  type="text"
                  placeholder="Type skill and press Enter (e.g., Java, Spring Boot)"
                />
              </div>
              <small>Must-have technical skills</small>
            </div>
  
            <!-- Nice-to-Have Skills -->
            <div class="form-group">
              <label>
                <i class="fas fa-plus-circle"></i>
                Nice-to-Have Skills
              </label>
              <div class="skills-input">
                <div class="skills-tags">
                  <span
                    v-for="(skill, index) in formData.criteria.nice_to_have_skills"
                    :key="index"
                    class="skill-tag nice-to-have"
                  >
                    {{ skill }}
                    <button @click="removeSkill(index, 'nice_to_have_skills')" type="button">×</button>
                  </span>
                </div>
                <input
                  v-model="niceToHaveInput"
                  @keydown.enter.prevent="addSkill('nice_to_have_skills')"
                  @keydown.comma.prevent="addSkill('nice_to_have_skills')"
                  type="text"
                  placeholder="Optional skills that are a plus"
                />
              </div>
              <small>Bonus skills (won't exclude candidates)</small>
            </div>
  
            <!-- Two Column Layout -->
            <div class="form-row">
              <!-- Seniority -->
              <div class="form-group">
                <label>
                  <i class="fas fa-star"></i>
                  Seniority Level *
                </label>
                <select v-model="formData.criteria.seniority" required>
                  <option value="">Select...</option>
                  <option value="junior">Junior (0-2 years)</option>
                  <option value="confirme">Confirmed (2-5 years)</option>
                  <option value="senior">Senior (5+ years)</option>
                </select>
              </div>
  
              <!-- Location -->
              <div class="form-group">
                <label>
                  <i class="fas fa-map-marker-alt"></i>
                  Location
                </label>
                <input
                  v-model="formData.criteria.location"
                  type="text"
                  placeholder="e.g., Casablanca, Paris, Remote"
                />
              </div>
            </div>
  
            <!-- Experience Range -->
            <div class="form-row">
              <div class="form-group">
                <label>
                  <i class="fas fa-clock"></i>
                  Min Experience (years) *
                </label>
                <input
                  v-model.number="minYears"
                  type="number"
                  min="0"
                  max="30"
                  placeholder="0"
                  required
                />
              </div>
  
              <div class="form-group">
                <label>
                  <i class="fas fa-clock"></i>
                  Max Experience (years)
                </label>
                <input
                  v-model.number="maxYears"
                  type="number"
                  min="0"
                  max="30"
                  placeholder="Leave empty for no max"
                />
              </div>
            </div>
  
            <!-- Additional Context -->
            <div class="form-group">
              <label>
                <i class="fas fa-info-circle"></i>
                Additional Context
              </label>
              <textarea
                v-model="formData.criteria.additional_context"
                rows="3"
                placeholder="Industry specifics, company culture, special requirements, certifications, etc."
              ></textarea>
              <small>Any other relevant information for matching</small>
            </div>
  
            <!-- Match Threshold -->
            <div class="form-group">
              <label>
                <i class="fas fa-percentage"></i>
                Match Threshold
              </label>
              <div class="threshold-input">
                <input
                  v-model.number="formData.match_threshold"
                  type="range"
                  min="0.5"
                  max="0.95"
                  step="0.05"
                />
                <span class="threshold-value">{{ (formData.match_threshold * 100).toFixed(0) }}%</span>
              </div>
              <small>Higher = stricter matching ({{ thresholdLabel }})</small>
            </div>
  
            <!-- Auto Refresh -->
            <div class="form-group checkbox-group">
              <label class="checkbox-label">
                <input
                  v-model="formData.auto_refresh"
                  type="checkbox"
                />
                <span>
                  <i class="fas fa-sync-alt"></i>
                  Auto-refresh daily
                </span>
              </label>
              <small>Automatically add new matching candidates each day</small>
            </div>
            <!-- Semantic Search -->
            <div class="form-group checkbox-group">
                <label class="checkbox-label">
                <input v-model="formData.criteria.use_semantic_search" type="checkbox" />
                <span>
                    <i class="fas fa-brain"></i>
                    Use semantic/AI matching
                </span>
                </label>
                <small>Uses AI to understand meaning beyond keywords (slower but smarter)</small>
            </div>
            <!-- Advanced: Semantic Weights -->
<div v-if="formData.criteria.use_semantic_search" class="form-group advanced-weights-section">
    <button 
      type="button" 
      @click="showAdvancedWeights = !showAdvancedWeights" 
      class="advanced-toggle"
    >
      <i class="fas" :class="showAdvancedWeights ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
      Advanced: Tune matching weights
      <span class="badge-pill" :class="{ 'badge-warning': !weightsValid }">
        {{ weightsValid ? '✓' : '⚠' }} {{ weightsSum }}%
      </span>
    </button>
    
    <transition name="slide-down">
      <div v-if="showAdvancedWeights" class="weights-tuner">
        <p class="tuner-hint">
          <i class="fas fa-info-circle"></i>
          Adjust how much each factor matters. Must total 100%.
        </p>
        
        <!-- Job Title Weight -->
        <div class="weight-control">
          <label>
            <i class="fas fa-briefcase"></i>
            Job Title Match
            <span class="weight-value">{{ semanticWeights.job_title }}%</span>
          </label>
          <input 
            v-model.number="semanticWeights.job_title" 
            type="range" 
            min="0" 
            max="100" 
            step="5"
            class="weight-slider"
          />
          <small>How important is matching their past job titles?</small>
        </div>
        
        <!-- Responsibilities Weight -->
        <div class="weight-control">
          <label>
            <i class="fas fa-tasks"></i>
            Responsibilities Match
            <span class="weight-value">{{ semanticWeights.responsibilities }}%</span>
          </label>
          <input 
            v-model.number="semanticWeights.responsibilities" 
            type="range" 
            min="0" 
            max="100" 
            step="5"
            class="weight-slider"
          />
          <small>How important is matching what they've done day-to-day?</small>
        </div>
        
        <!-- Skills Weight -->
        <div class="weight-control">
          <label>
            <i class="fas fa-code"></i>
            Skills Match
            <span class="weight-value">{{ semanticWeights.skills }}%</span>
          </label>
          <input 
            v-model.number="semanticWeights.skills" 
            type="range" 
            min="0" 
            max="100" 
            step="5"
            class="weight-slider"
          />
          <small>How important is matching their technical skills?</small>
        </div>
        
        <!-- Additional Context Weight -->
        <div class="weight-control">
          <label>
            <i class="fas fa-info-circle"></i>
            Context Match
            <span class="weight-value">{{ semanticWeights.additional_context }}%</span>
          </label>
          <input 
            v-model.number="semanticWeights.additional_context" 
            type="range" 
            min="0" 
            max="100" 
            step="5"
            class="weight-slider"
          />
          <small>How important are certs, culture fit, soft skills?</small>
        </div>
        
        <!-- Validation Warning -->
        <div v-if="!weightsValid" class="weight-warning">
          <i class="fas fa-exclamation-triangle"></i>
          Weights must total 100%. Currently: {{ weightsSum }}%
        </div>
        
        <!-- Quick Presets -->
        <div class="weight-presets">
          <button type="button" @click="applyPreset('balanced')" class="preset-btn">
            <i class="fas fa-balance-scale"></i> Balanced
          </button>
          <button type="button" @click="applyPreset('title-focused')" class="preset-btn">
            <i class="fas fa-briefcase"></i> Title-Focused
          </button>
          <button type="button" @click="applyPreset('skills-focused')" class="preset-btn">
            <i class="fas fa-code"></i> Skills-Focused
          </button>
        </div>
      </div>
    </transition>
  </div>
  
            <!-- Buttons -->
            <div class="form-actions">
              <button type="button" @click="$emit('close')" class="btn-secondary">
                Cancel
              </button>
              <button type="submit" :disabled="creating || !formData.name" class="btn-primary">
                <i class="fas fa-fire" :class="{ 'fa-spin fa-spinner': creating }"></i>
                {{ creating ? (isEditing ? 'Updating...' : 'Creating...') : (isEditing ? 'Update Hot List' : 'Create Hot List') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import axios from 'axios';
  
  export default {
    name: 'CreateHotListModal',
    props: {
    initialData: {
      type: Object,
      default: null
    }
  },
  data() {
  // Pre-fill if editing
  if (this.initialData) {
    const years = (months) => months ? Math.floor(months / 12) : null;
    
    return {
        sharingMode: 'personal',  // 'personal' or 'project'
      selectedProjectId: null,
      availableProjects: [],
        showAdvancedWeights: false,  // NEW
    semanticWeights: {            // NEW
      job_title: 30,
      responsibilities: 40,
      skills: 20,
      additional_context: 10
    },
      isEditing: true,
      hotListId: this.initialData.hot_list_id,
      formData: {
        name: this.initialData.name,
        criteria: {
          job_title: this.initialData.criteria.job_title || '',
          job_description: this.initialData.criteria.job_description || [],
          skills: this.initialData.criteria.skills || [],
          nice_to_have_skills: this.initialData.criteria.nice_to_have_skills || [],
          seniority: this.initialData.criteria.seniority || '',
          min_experience_months: this.initialData.criteria.min_experience_months || null,
          max_experience_months: this.initialData.criteria.max_experience_months || null,
          location: this.initialData.criteria.location || '',
          additional_context: this.initialData.criteria.additional_context || ''
        },
        match_threshold: this.initialData.match_threshold || 0.70,
        auto_refresh: this.initialData.auto_refresh !== false
      },
      skillInput: '',
      responsibilityInput: '',
      niceToHaveInput: '',
      minYears: years(this.initialData.criteria.min_experience_months),
      maxYears: years(this.initialData.criteria.max_experience_months),
      creating: false
    };
  }
  
  // Default empty form
  return { 
    sharingMode: 'personal',  // 'personal' or 'project'
      selectedProjectId: null,
      availableProjects: [],
    showAdvancedWeights: false,  // NEW
    semanticWeights: {            // NEW
      job_title: 30,
      responsibilities: 40,
      skills: 20,
      additional_context: 10
    },
    isEditing: false,
    hotListId: null,
    formData: {
      name: '',
      criteria: {
        job_title: '',
        job_description: [],
        skills: [],
        nice_to_have_skills: [],
        seniority: '',
        min_experience_months: null,
        max_experience_months: null,
        location: '',
        additional_context: ''
      },
      match_threshold: 0.70,
      auto_refresh: true
    },
        responsibilityInput: '',
    skillInput: '',
    niceToHaveInput: '',
    minYears: null,
    maxYears: null,
    creating: false
  };
},
    
    computed: {
    thresholdLabel() {
        const threshold = this.formData.match_threshold;
        if (threshold >= 0.85) return 'Very Strict';
        if (threshold >= 0.75) return 'Strict';
        if (threshold >= 0.65) return 'Balanced';
        return 'Loose';
      },
    weightsSum() {
        return Object.values(this.semanticWeights).reduce((a, b) => a + b, 0);
    },
    
    weightsValid() {
        return Math.abs(this.weightsSum - 100) < 1;
    }

    },
    async mounted() {
    // ✅ Load agent's active projects
    await this.loadAvailableProjects();
  },
    methods: {
      async loadAvailableProjects() {
      try {
        const response = await axios.get('/api/v1/agent/my-projects');
        this.availableProjects = response.data.projects || [];
        console.log(`✅ Loaded ${this.availableProjects.length} available projects for hot list sharing`);
      } catch (error) {
        console.error('Error loading projects:', error);
        this.availableProjects = [];
      }
    },
    
    onSharingModeChange() {
      // Clear project selection when switching to personal
      if (this.sharingMode === 'personal') {
        this.selectedProjectId = null;
      }
      console.log('Sharing mode changed to:', this.sharingMode);
    },
    
        // NEW: Add responsibility method
    addResponsibility() {
      const resp = this.responsibilityInput.trim();
      if (resp && !this.formData.criteria.job_description.includes(resp)) {
        this.formData.criteria.job_description.push(resp);
        this.responsibilityInput = '';
      }
    },
    
    // NEW: Remove responsibility method  
    removeResponsibility(index) {
      this.formData.criteria.job_description.splice(index, 1);
    },
        applyPreset(preset) {
    const presets = {
      'balanced': {
        job_title: 30,
        responsibilities: 40,
        skills: 20,
        additional_context: 10
      },
      'title-focused': {
        job_title: 50,
        responsibilities: 25,
        skills: 20,
        additional_context: 5
      },
      'skills-focused': {
        job_title: 20,
        responsibilities: 30,
        skills: 45,
        additional_context: 5
      }
    };
    
    this.semanticWeights = { ...presets[preset] };
  },
      addSkill(type = 'skills') {
        const input = type === 'skills' ? this.skillInput : this.niceToHaveInput;
        const skill = input.trim();
        
        if (skill && !this.formData.criteria[type].includes(skill)) {
          this.formData.criteria[type].push(skill);
          
          if (type === 'skills') {
            this.skillInput = '';
          } else {
            this.niceToHaveInput = '';
          }
        }
      },
      
      removeSkill(index, type = 'skills') {
        this.formData.criteria[type].splice(index, 1);
      },
      
      async createHotList() {
  try {
    this.creating = true;
    
    // Convert years to months
    if (this.minYears) {
      this.formData.criteria.min_experience_months = this.minYears * 12;
    }
    if (this.maxYears) {
      this.formData.criteria.max_experience_months = this.maxYears * 12;
    }
    
    // Remove empty fields
    const cleanCriteria = {};
    for (const [key, value] of Object.entries(this.formData.criteria)) {
      if (value !== null && value !== '' && !(Array.isArray(value) && value.length === 0)) {
        cleanCriteria[key] = value;
      }
    }
    // Add semantic weights if semantic search is enabled
    if (cleanCriteria.use_semantic_search) {
      // Validate weights
      if (!this.weightsValid) {
        alert(`Semantic weights must total 100%. Currently: ${this.weightsSum}%`);
        return;
      }
      
      // Convert percentages to decimals
      cleanCriteria.semantic_weights = {
        job_title: this.semanticWeights.job_title / 100,
        responsibilities: this.semanticWeights.responsibilities / 100,
        skills: this.semanticWeights.skills / 100,
        additional_context: this.semanticWeights.additional_context / 100
      };
    }
    const payload = {
      name: this.formData.name,
      criteria: cleanCriteria,
      match_threshold: this.formData.match_threshold,
      auto_refresh: this.formData.auto_refresh
    };
     
        // ✅ Include project_id if sharing
        const params = {};
        if (this.sharingMode === 'project' && this.selectedProjectId) {
          params.project_id = this.selectedProjectId;
        }
    
    let response;
    if (this.isEditing) {
      // UPDATE existing
      response = await axios.put(`/api/v1/hot-lists/${this.hotListId}`, payload,{ params });
    } else {
      // CREATE new
      response = await axios.post('/api/v1/hot-lists', payload,{ params });
    }
    
    console.log(this.isEditing ? '✅ Hot list updated' : '✅ Hot list created', response.data);
    
    this.$emit('created', response.data);
    
  } catch (error) {
    console.error('❌ Error with hot list:', error);
  } finally {
    this.creating = false;
  }
}
    }
  };
  </script>
  
  <style scoped>
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2000;
    padding: 1rem;
  }
  
  .modal-content {
    background: white;
    border-radius: 1rem;
    width: 100%;
    max-width: 600px;
    max-height: 90vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
  
  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.5rem;
    border-bottom: 1px solid #e9ecef;
    background: linear-gradient(135deg, #ff6b6b 0%, #ff8c42 100%);
    color: white;
  }
  
  .modal-header h3 {
    margin: 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  .close-btn {
    background: transparent;
    border: none;
    color: white;
    font-size: 2rem;
    cursor: pointer;
    opacity: 0.8;
    transition: opacity 0.2s;
    line-height: 1;
  }
  
  .close-btn:hover {
    opacity: 1;
  }
  
  .modal-body {
    padding: 1.5rem;
    overflow-y: auto;
  }
  
  .modal-description {
    color: #6c757d;
    margin-bottom: 1.5rem;
    padding: 1rem;
    background: #f8f9fa;
    border-radius: 0.5rem;
    border-left: 3px solid #ff6b6b;
  }
  
  .form-group {
    margin-bottom: 1.5rem;
  }
  
  .form-group label {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: #495057;
  }
  
  .form-group input[type="text"],
  .form-group input[type="number"],
  .form-group select,
  .form-group textarea {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid #ced4da;
    border-radius: 0.5rem;
    font-size: 1rem;
    transition: border-color 0.2s;
  }
  
  .form-group input:focus,
  .form-group select:focus,
  .form-group textarea:focus {
    outline: none;
    border-color: #ff6b6b;
  }
  
  .form-group small {
    display: block;
    margin-top: 0.25rem;
    color: #6c757d;
    font-size: 0.875rem;
  }
  
  /* Skills Input */
  .skills-input {
    border: 1px solid #ced4da;
    border-radius: 0.5rem;
    padding: 0.5rem;
    min-height: 3rem;
  }
  
  .skills-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
  }
  
  .skill-tag {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.375rem 0.75rem;
    border-radius: 1rem;
    font-size: 0.875rem;
  }
  
  .skill-tag.required {
    background: #ff6b6b;
    color: white;
  }
  
  .skill-tag.nice-to-have {
    background: #51cf66;
    color: white;
  }
  
  .skill-tag button {
    background: transparent;
    border: none;
    color: white;
    font-size: 1.25rem;
    cursor: pointer;
    padding: 0;
    line-height: 1;
    opacity: 0.8;
  }
  
  .skill-tag button:hover {
    opacity: 1;
  }
  
  .skills-input input {
    border: none;
    outline: none;
    width: 100%;
    padding: 0.25rem;
  }
  
  /* Form Row */
  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
  }
  
  /* Threshold Input */
  .threshold-input {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
  
  .threshold-input input[type="range"] {
    flex: 1;
  }
  
  .threshold-value {
    font-weight: 600;
    color: #ff6b6b;
    min-width: 3rem;
    text-align: right;
  }
  
  /* Checkbox Group */
  .checkbox-group {
    padding: 1rem;
    background: #f8f9fa;
    border-radius: 0.5rem;
  }
  
  .checkbox-label {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    cursor: pointer;
    font-weight: normal;
    margin-bottom: 0;
  }
  
  .checkbox-label input[type="checkbox"] {
    width: auto;
    cursor: pointer;
  }
  
  .checkbox-label span {
    display: flex;
    align-items: center;
    gap: 0.375rem;
  }
  
  /* Form Actions */
  .form-actions {
    display: flex;
    gap: 1rem;
    justify-content: flex-end;
    margin-top: 2rem;
    padding-top: 1.5rem;
    border-top: 1px solid #e9ecef;
  }
  
  .btn-secondary,
  .btn-primary {
    padding: 0.75rem 1.5rem;
    border: none;
    border-radius: 0.5rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  .btn-secondary {
    background: #6c757d;
    color: white;
  }
  
  .btn-secondary:hover {
    background: #5a6268;
  }
  
  .btn-primary {
    background: linear-gradient(135deg, #ff6b6b 0%, #ff8c42 100%);
    color: white;
  }
  
  .btn-primary:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(255, 107, 107, 0.3);
  }
  
  .btn-primary:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  /* Responsive */
  @media (max-width: 640px) {
    .form-row {
      grid-template-columns: 1fr;
    }
    
    .modal-content {
      max-width: 100%;
      border-radius: 0;
    }
  }
  



  /*weighst*/
  .advanced-weights-section {
    background: #f0f7ff;
    border: 1px solid #c3ddfd;
    border-radius: 0.5rem;
    padding: 0;
    margin-top: 0.5rem;
  }
  
  .advanced-toggle {
    width: 100%;
    padding: 0.75rem 1rem;
    background: transparent;
    border: none;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
    font-weight: 500;
    color: #495057;
    transition: background 0.2s;
  }
  
  .advanced-toggle:hover {
    background: rgba(0, 0, 0, 0.02);
  }
  
  .advanced-toggle .badge-pill {
    margin-left: auto;
    padding: 0.25rem 0.5rem;
    border-radius: 1rem;
    font-size: 0.75rem;
    background: #51cf66;
    color: white;
  }
  
  .advanced-toggle .badge-warning {
    background: #ffa94d;
  }
  
  .weights-tuner {
    padding: 1rem;
    border-top: 1px solid #c3ddfd;
  }
  
  .tuner-hint {
    background: white;
    border-left: 3px solid #339af0;
    padding: 0.75rem;
    margin-bottom: 1rem;
    border-radius: 0.25rem;
    font-size: 0.875rem;
    color: #495057;
  }
  
  .weight-control {
    margin-bottom: 1.25rem;
  }
  
  .weight-control label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: #495057;
  }
  
  .weight-value {
    margin-left: auto;
    font-size: 1rem;
    color: #339af0;
    font-weight: 700;
  }
  
  .weight-slider {
    width: 100%;
    height: 6px;
    border-radius: 3px;
    background: linear-gradient(to right, #c3ddfd 0%, #339af0 100%);
    outline: none;
    -webkit-appearance: none;
  }
  
  .weight-slider::-webkit-slider-thumb {
    appearance: none;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #339af0;
    cursor: pointer;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
  
  .weight-slider::-moz-range-thumb {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #339af0;
    cursor: pointer;
    border: none;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
  
  .weight-warning {
    background: #fff3cd;
    border: 1px solid #ffa94d;
    border-radius: 0.375rem;
    padding: 0.75rem;
    color: #856404;
    font-size: 0.875rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 1rem;
  }
  
  .weight-presets {
    display: flex;
    gap: 0.5rem;
    margin-top: 1rem;
    flex-wrap: wrap;
  }
  
  .preset-btn {
    flex: 1;
    min-width: 120px;
    padding: 0.5rem 1rem;
    background: white;
    border: 1px solid #c3ddfd;
    border-radius: 0.375rem;
    cursor: pointer;
    font-size: 0.875rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.375rem;
    color: #495057;
    transition: all 0.2s;
  }
  
  .preset-btn:hover {
    background: #339af0;
    color: white;
    border-color: #339af0;
  }
  
  .slide-down-enter-active, .slide-down-leave-active {
    transition: all 0.3s ease;
    max-height: 600px;
    overflow: hidden;
  }
  
  .slide-down-enter-from, .slide-down-leave-to {
    max-height: 0;
    opacity: 0;
  }

  /* Add to existing styles */
.skill-tag.responsibility {
    background: #4dabf7;  /* Blue color for responsibilities */
    color: white;
  }

  .sharing-section {
    background: #f0f7ff;
    border: 1px solid #c3ddfd;
    border-radius: 0.5rem;
    padding: 1rem;
  }
  
  .sharing-options {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin-top: 0.5rem;
  }
  
  
.radio-option {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem;
  background: white;
  border: 2px solid #e9ecef;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s;
}

.radio-option:hover {
  border-color: #339af0;
  background: #f8f9fa;
}

.radio-option.selected {
  border-color: #339af0;
  background: #e7f5ff;
}

.radio-option input[type="radio"] {
  margin-top: 0.25rem;
  cursor: pointer;
}

.no-projects-message {
  padding: 1rem;
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 0.375rem;
  color: #856404;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.project-selector {
  margin-top: 1rem;
  padding: 1rem;
  background: white;
  border-radius: 0.5rem;
}

.project-selector label {
  display: block;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #495057;
}

.project-selector select {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  font-size: 0.875rem;
}

.slide-down-enter-active, .slide-down-leave-active {
  transition: all 0.3s ease;
}

.slide-down-enter-from, .slide-down-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
  </style>